import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Input, InputField } from "@repo/components/src/ui/input";
import {
  AsYouType,
  CountryCode,
  getExampleNumber,
  parseIncompletePhoneNumber,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import { AlertCircleIcon } from "lucide-react-native";
import { ComponentProps, useCallback } from "react";
import { Controller, FieldValues, get } from "react-hook-form";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";
import { CountrySelectorStyleProps } from "./countrySelector/types";
import { countryCodesV2 } from "./utils";

import * as yup from "yup";
import { Box } from "../../ui/box";
import { HStack } from "../../ui/hstack";
import { phoneSchema } from "../../utils/yup";
import CountrySelector from "./countrySelector";

type InputFieldProps = ComponentProps<typeof Input>;

type PhoneValue = yup.InferType<typeof phoneSchema>;

export type FormInputPhoneProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, PhoneValue | undefined | null> & {
    inputProps?: InputFieldProps;
    labelProps?: ComponentProps<typeof FormControlLabelText>;
    countrySelectorProps?: CountrySelectorStyleProps;
    onBlur?: () => void;
  };

export default function FormInputPhone<TFieldValues extends FieldValues>(
  props: FormInputPhoneProps<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    required,
    disabled: disabledProp = false,
    className,
    size,
    labelSize = "md",
    labelProps = {},
    inputProps = {},
    onBlur: onBlurProp,
  } = props;

  const findSelectedCountry = useCallback((countryCode: string) => {
    return countryCodesV2.find((c) => c.code === countryCode);
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      disabled={disabledProp}
      render={({
        field: { onChange, value, ref, onBlur, disabled },
        fieldState: { error },
      }) => {
        const selectedCountry = findSelectedCountry(value?.countryCode ?? "");

        const handleCountryChange = (countryCode: string) => {
          onChange({
            ...value,
            countryCode: countryCode,
          });
        };

        const handleNumberChange = (text: string) => {
          const parsed = parseIncompletePhoneNumber(text);
          onChange({
            ...value,
            number: parsed,
          });
        };

        const displayNumber = new AsYouType(
          (value?.countryCode as CountryCode) ?? "CL",
        ).input(value?.number ?? "");
        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={disabled}
            className={className}
            size={size}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize} {...labelProps}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <HStack space="sm" className="w-full">
              <CountrySelector
                formProps={props}
                disabled={disabled}
                selectedCountry={selectedCountry}
                handleCountryChange={handleCountryChange}
              />
              <Box className="flex-1">
                <Input
                  {...inputProps}
                  isDisabled={disabled}
                  isInvalid={!!error}
                  size={size}
                >
                  <InputField
                    ref={focusScrollRef(ref)}
                    onBlur={() => {
                      onBlur();
                      onBlurProp?.();
                    }}
                    placeholder={
                      getExampleNumber(
                        selectedCountry?.code as CountryCode,
                        examples,
                      )?.format("NATIONAL") ?? ""
                    }
                    value={displayNumber}
                    onChangeText={handleNumberChange}
                    keyboardType="phone-pad"
                    textContentType="telephoneNumber"
                  />
                </Input>
              </Box>
            </HStack>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>
                {/* The value is an object: a schema may put its error on one of
                    its parts or on the whole value. */}
                {get(error, "number.message") ??
                  get(error, "countryCode.message") ??
                  error?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
